/**
 * Idle prefetch — pay the expensive first-navigation costs during idle time
 * after app startup, instead of when the user clicks Companies/People.
 *
 * Why (measured on staging): every GraphQL request pays ~230-280ms geographic
 * RTT to us-west-2, the first OpenSearch search of a session is cold
 * (~290-460ms vs 20-95ms warm), and a page-load burst of ~11 concurrent
 * requests queues on the backend's 4 uvicorn workers. So we warm the caches
 * BEFORE the user navigates — and deliberately NOT all at once.
 *
 * Tier 1 — cacheable queries (real cache hits when the user navigates):
 *  - getCrmIngestionFields: the custom-fields hooks are cache-first, so one
 *    prefetch turns every grid/detail-sheet consumer into a cache hit.
 *  - getAccountsGridSummaries / getContactsGridSummaries: variables must match
 *    the pages' own requests byte-for-byte (including the visible-column set
 *    derived from persisted column prefs), or the cache entry is pure waste.
 *    Computed via the same shared config + derivation the pages use.
 *  - getUserLists with PeoplePage's exact variables.
 *
 * Tier 2 — backend/OpenSearch warmers (results discarded):
 *  - getAccounts / getContacts page 1 with the pages' default variables. The
 *    grids read rows via useWindowedConnection, which is deliberately
 *    `no-cache` (see use-windowed-connection.ts fetchPage) — so there is no
 *    Apollo cache entry to populate. This tier exists purely to warm
 *    OpenSearch/Mongo (query caches, page cache) so the user's real first
 *    grid query runs warm.
 *
 * Scheduling: waits a minimum delay after mount plus a browser idle callback
 * so it never competes with the landing page's own request burst, then fires
 * the queries SEQUENTIALLY (the backend queues bursts on 4 workers — an
 * 11-wide prefetch burst would recreate the exact problem this solves).
 * Runs once per workspace per session; every query failure is a silent no-op.
 */

import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import {
  CrmIngestionObjectType,
  CrmIngestionPlatform,
  GetAccountsDocument,
  GetAccountsGridSummariesDocument,
  GetContactsDocument,
  GetContactsGridSummariesDocument,
  GetCrmIngestionFieldsDocument,
  GetCrmIngestionFieldsQuery,
  GetUserListsDocument,
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { useCurrentUser } from '@/lib/hooks/use-current-user';
import { isLovablePreview } from '@/lib/utils/environment';
import { customFieldsFromCrmIngestion } from '@/lib/utils/crm-custom-fields';
import {
  deriveVisibleColumns,
  tableColumnsStorageSuffix,
  type ColumnDef,
} from '@/hooks/useTableColumns';
import { workspaceStorageKey } from '@/hooks/useWorkspaceLocalStorage';
import { WINDOWED_SCROLL_PAGE_SIZE } from '@/lib/hooks/use-windowed-connection';
import {
  COMPANY_GRID_COLUMNS,
  COMPANY_TABLE_STORAGE_KEY,
  companyFillRateKeyFor,
  PEOPLE_GRID_COLUMNS,
  PEOPLE_TABLE_STORAGE_KEY,
  peopleFillRateKeyFor,
} from '@/lib/grid/grid-column-config';

/** Minimum quiet period after mount before the prefetch may start. */
export const IDLE_PREFETCH_MIN_DELAY_MS = 4000;
/** requestIdleCallback timeout: run even if the browser never reports idle. */
const IDLE_CALLBACK_TIMEOUT_MS = 10_000;
/** Fallback extra delay when requestIdleCallback is unavailable (Safari). */
const IDLE_FALLBACK_DELAY_MS = 1000;

const GRID_CONFIG = {
  account: {
    standardColumns: COMPANY_GRID_COLUMNS,
    storageKey: COMPANY_TABLE_STORAGE_KEY,
    fillRateKeyFor: companyFillRateKeyFor,
    objectType: CrmIngestionObjectType.Account,
  },
  contact: {
    standardColumns: PEOPLE_GRID_COLUMNS,
    storageKey: PEOPLE_TABLE_STORAGE_KEY,
    fillRateKeyFor: peopleFillRateKeyFor,
    objectType: CrmIngestionObjectType.Contact,
  },
} as const;

/** Read the same persisted column prefs useTableColumns/useWorkspaceLocalStorage use. */
function readPersistedTableCols(workspaceId: string, storageKey: string): unknown {
  try {
    const raw = localStorage.getItem(
      workspaceStorageKey(workspaceId, tableColumnsStorageSuffix(storageKey)),
    );
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Predict the exact `columns` variable CompaniesPage/PeoplePage will send to
 * getAccounts/getContactsGridSummaries: standard columns + backend custom
 * fields, reconciled with persisted column prefs (order/hidden/session-created
 * customs) via the same derivation useTableColumns performs, then mapped
 * through the page's fill-rate key mapping.
 *
 * Note: CompaniesPage gates the "street" column behind a feature flag, which
 * we ignore here — "street" has no fill-rate key, so its presence never
 * changes the mapped output.
 *
 * Exported for tests.
 */
export function computeGridSummaryColumns(
  entity: 'account' | 'contact',
  workspaceId: string,
  crmFieldsData: GetCrmIngestionFieldsQuery,
): string[] {
  const cfg = GRID_CONFIG[entity];
  const customFields = customFieldsFromCrmIngestion(crmFieldsData, cfg.objectType);
  const initialColumns = [
    ...cfg.standardColumns,
    ...(customFields as unknown as ColumnDef[]), // same cast the pages apply
  ];
  // Default-hidden columns are read off the ColumnDefs in initialColumns, so
  // there's nothing to pass here.
  const visible = deriveVisibleColumns(
    initialColumns,
    readPersistedTableCols(workspaceId, cfg.storageKey),
  );
  return visible
    .map(c => cfg.fillRateKeyFor(c.key))
    .filter((k): k is string => k !== null);
}

interface IdlePrefetchArgs {
  client: ApolloClient<object>;
  workspaceId: string;
  userId: string;
}

/** client.query wrapper: prefetch failures must be silent no-ops. */
async function safeQuery<TData>(
  client: ApolloClient<object>,
  options: Parameters<ApolloClient<object>['query']>[0],
): Promise<TData | null> {
  try {
    const result = await client.query(options);
    return (result.data as TData) ?? null;
  } catch {
    return null; // silent no-op: prefetch must never surface errors
  }
}

/** Exported for tests. Queries run sequentially — see module docs for why. */
export async function runIdlePrefetch({
  client,
  workspaceId,
  userId,
}: IdlePrefetchArgs): Promise<void> {
  // ── Tier 1: cacheable queries ───────────────────────────────────────────
  // Custom-field definitions first: the *-custom-fields hooks are cache-first
  // with these exact variables, and the grid summary column sets below depend
  // on the custom fields.
  const crmFieldsData = await safeQuery<GetCrmIngestionFieldsQuery>(client, {
    query: GetCrmIngestionFieldsDocument,
    variables: {
      workspaceId: parseInt(workspaceId),
      platform: CrmIngestionPlatform.Coffee, // matches use-*-custom-fields hooks
      first: 100,
    },
  });

  // Grid summaries: only when we KNOW the final column set. If the fields
  // query failed we cannot guarantee a byte-for-byte variable match with the
  // page's own request, and a mismatched cache entry is pure waste.
  if (crmFieldsData) {
    await safeQuery(client, {
      query: GetAccountsGridSummariesDocument,
      variables: {
        workspaceId: parseInt(workspaceId),
        // matches use-company-grid-summaries filter with CompaniesPage's empty landing filter
        filter: { workspaceId: String(parseInt(workspaceId)) },
        columns: computeGridSummaryColumns('account', workspaceId, crmFieldsData),
      },
    });
    await safeQuery(client, {
      query: GetContactsGridSummariesDocument,
      variables: {
        workspaceId: parseInt(workspaceId),
        // matches use-people-grid-summaries filter with PeoplePage's empty landing filter
        filter: { workspaceId: String(parseInt(workspaceId)) },
        columns: computeGridSummaryColumns('contact', workspaceId, crmFieldsData),
      },
    });
  }

  // PeoplePage's lists query, exact variables (cache-and-network there reads
  // this cache entry synchronously while revalidating in the background).
  await safeQuery(client, {
    query: GetUserListsDocument,
    variables: {
      filter: { workspaceId, modelType: 'CONTACT', userId },
      sort: [{ field: 'createTs', sort: 'DESC' }],
    },
  });

  // ── Tier 2: backend/OpenSearch warmers ──────────────────────────────────
  // Page 1 of each grid with the pages' default production variables. The
  // grids fetch rows through useWindowedConnection with fetchPolicy 'no-cache'
  // (deliberate — sparse windowed store owns row state), so these results are
  // DISCARDED: the sole purpose is to warm OpenSearch/Mongo so the user's real
  // first query is warm instead of paying the ~290-460ms cold-search penalty.
  const gridSort = [
    { field: 'lastEmailTime', sort: 'DESC' }, // pages' production default sort
    { field: '_id', sort: 'DESC' }, // secondary sort for consistent ordering
  ];
  await safeQuery(client, {
    query: GetAccountsDocument,
    variables: {
      filter: { workspaceId, deleted: false }, // matches use-companies.ts
      sort: gridSort,
      first: WINDOWED_SCROLL_PAGE_SIZE,
      after: '0',
    },
    fetchPolicy: 'no-cache',
  });
  await safeQuery(client, {
    query: GetContactsDocument,
    variables: {
      filter: { workspaceId, deleted: false }, // matches use-contacts.ts
      sort: gridSort,
      first: WINDOWED_SCROLL_PAGE_SIZE,
      after: '0',
    },
    fetchPolicy: 'no-cache',
  });
}

// Once per workspace per browser session. Module-level so AppLayout remounts
// (it remounts on every route change) never re-fire the prefetch.
const prefetchedWorkspaces = new Set<string>();

/** Test-only: clear the once-per-workspace sentinel. */
export function __resetIdlePrefetchForTests(): void {
  prefetchedWorkspaces.clear();
}

/**
 * Mount from an always-mounted root (AppLayout). Self-gates: no-op in Lovable
 * preview and until workspaceId + current user resolve.
 */
export function useIdlePrefetch(): void {
  const client = useApolloClient();
  const workspaceId = useWorkspaceId();
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id;

  useEffect(() => {
    if (isLovablePreview()) return;
    if (!workspaceId || !userId) return;
    if (prefetchedWorkspaces.has(workspaceId)) return;
    // Claim the slot synchronously so concurrent mounts can't double-fire.
    prefetchedWorkspaces.add(workspaceId);

    let started = false;
    let idleHandle: number | null = null;
    let fallbackTimer: number | null = null;

    const start = () => {
      started = true;
      // Fire-and-forget: once started, a route change/unmount doesn't cancel —
      // queries are client-scoped and every failure is already swallowed.
      void runIdlePrefetch({ client, workspaceId, userId });
    };

    // Minimum delay so we never compete with the landing page's own burst…
    const minDelayTimer = window.setTimeout(() => {
      // …then wait for actual browser idle (with a fallback for Safari).
      if (typeof window.requestIdleCallback === 'function') {
        idleHandle = window.requestIdleCallback(start, { timeout: IDLE_CALLBACK_TIMEOUT_MS });
      } else {
        fallbackTimer = window.setTimeout(start, IDLE_FALLBACK_DELAY_MS);
      }
    }, IDLE_PREFETCH_MIN_DELAY_MS);

    return () => {
      window.clearTimeout(minDelayTimer);
      if (idleHandle !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
      if (fallbackTimer !== null) window.clearTimeout(fallbackTimer);
      // AppLayout remounts on route changes: if the idle window never arrived
      // before unmount, release the slot so the next mount re-arms the timer.
      if (!started) prefetchedWorkspaces.delete(workspaceId);
    };
  }, [client, workspaceId, userId]);
}
